import React, { useRef, useState } from 'react';
import styles from '../../components/patient-attachments/styles.module.css';
import dayjs from 'dayjs';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import IconClose from '@mui/icons-material/Close';
import IconPhoto from '@mui/icons-material/PhotoCameraOutlined';
import IconAttach from '@mui/icons-material/AttachFileOutlined';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import Toast from '../../components/support/Toast';
import ColorPalette from '../../colorpalette/ColorPalette';
import { DataProviderInstance } from '../../api/DataProvider';
import { getKindIcon, getKindInfo, isImageKind } from '../../components/patient-attachments/attachmentKinds';
import { prepareUploadItem, releaseUploadItem, uploadAttachmentItems } from './uploadAttachments';

const DEFAULT_ACCEPT = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt';

/**
 * Popup genérico de upload de anexos do paciente.
 * O núcleo não sabe o que os satélites guardam: campos por arquivo vêm por
 * renderItemFields e viram campos do FormData em buildItemPayload.
 *
 * @param {number} patientId
 * @param {number} appointmentId proveniência (opcional)
 * @param {string} context 'general' | 'cranial_photo'
 * @param {number} contextId id no satélite (opcional)
 * @param {string} defaultDate data sugerida (YYYY-MM-DD)
 * @param {boolean} capture habilita "Tirar foto" (câmera traseira no celular)
 * @param {number} maxFiles limite por envio
 * @param {string} accept accept do input
 * @param {function} renderItemFields (file, value, onChange) => ReactNode
 * @param {function} buildItemPayload (value) => objeto com campos extras
 * @param {string} title título do popup
 * @param {function} OnHide (success) => void
 */
function PatientAttachmentUploadPopup(props) {
    const {
        patientId,
        appointmentId,
        context = 'general',
        contextId,
        defaultDate,
        capture = false,
        maxFiles = 10,
        accept = DEFAULT_ACCEPT,
        renderItemFields,
        buildItemPayload,
        title = 'Anexar arquivos',
        OnHide
    } = props;

    const { UploadPatientAttachment } = DataProviderInstance();

    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const nextId = useRef(1);

    const [items, setItems] = useState([]);
    const [preparing, setPreparing] = useState(false);
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState('');
    const [date, setDate] = useState(defaultDate ? dayjs(defaultDate) : dayjs());
    const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

    const showToast = (message, severity = 'error') => {
        setToast({ open: true, message: message, severity: severity });
    };

    async function filesChosenHandler(event) {
        const chosen = Array.from(event.target.files || []);
        event.target.value = '';

        if (chosen.length === 0) return;

        const room = maxFiles - items.length;

        if (room <= 0) {
            showToast(`Até ${maxFiles} arquivos por envio.`, 'warning');
            return;
        }

        const accepted = chosen.slice(0, room);

        if (accepted.length < chosen.length) {
            showToast(`Até ${maxFiles} arquivos por envio.`, 'warning');
        }

        setPreparing(true);

        try {
            const prepared = [];

            for (const file of accepted) {
                try {
                    prepared.push(await prepareUploadItem(file, nextId.current++));
                } catch (e) {
                    showToast(`Não foi possível preparar "${file.name}".`);
                }
            }

            setItems((current) => current.concat(prepared));
        } finally {
            setPreparing(false);
        }
    }

    function removeItem(id) {
        setItems((current) => current.filter((item) => {
            if (item.id === id) {
                releaseUploadItem(item);
            }

            return item.id !== id;
        }));
    }

    function updateItemValue(id, value) {
        setItems((current) => current.map(item => item.id === id ? { ...item, value: value } : item));
    }

    async function saveHandler() {
        if (items.length === 0) {
            showToast('Escolha ao menos um arquivo.', 'warning');
            return;
        }

        if (!date) {
            showToast('Informe a data do documento.', 'warning');
            return;
        }

        setSending(true);

        const base = {
            patientId: patientId,
            appointmentId: appointmentId,
            context: context,
            contextId: contextId,
            documentDate: date.format('YYYY-MM-DD')
        };

        const result = await uploadAttachmentItems(UploadPatientAttachment, items, base, {
            buildItemPayload: buildItemPayload,
            onProgress: (current, total) => setProgress(`Enviando ${current} de ${total}…`)
        });

        setSending(false);
        setProgress('');

        if (result.success > 0) {
            OnHide(true);
            return;
        }

        showToast(result.lastError || 'Não foi possível enviar os arquivos.');
    }

    function GetPreview(item) {
        const kindInfo = getKindInfo(item.kind);

        if (isImageKind(item.kind) && item.previewUrl) {
            return (
                <div className={styles.thumb} style={{ width: 56, height: 56 }}>
                    <img className={styles.thumbImage} src={item.previewUrl} alt={item.name} />
                </div>
            );
        }

        return (
            <div className={styles.thumb} style={{ width: 56, height: 56, backgroundColor: kindInfo.background }}>
                {getKindIcon(item.kind, 26)}
            </div>
        );
    }

    function GetContent() {
        return (
            <Box className={styles.uploadContainer}>
                <Box className={styles.uploadActions}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Data do documento"
                            value={date}
                            format="DD/MM/YYYY"
                            disableFuture={true}
                            onChange={(value) => setDate(value)}
                        />
                    </LocalizationProvider>

                    {capture && (
                        <CustomButton
                            variant="outlined"
                            style="primary"
                            label="Tirar foto"
                            icon={<IconPhoto />}
                            onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
                            enabled={!sending}
                        />
                    )}

                    <CustomButton
                        variant="outlined"
                        style="primary"
                        label="Escolher arquivos"
                        icon={<IconAttach />}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        enabled={!sending}
                    />

                    <Typography component="span" className={styles.uploadHint}>
                        {`Até ${maxFiles} arquivos por envio`}
                    </Typography>
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={accept}
                    style={{ display: 'none' }}
                    onChange={filesChosenHandler}
                />

                {capture && (
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={filesChosenHandler}
                    />
                )}

                {preparing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                            Preparando arquivos…
                        </Typography>
                    </Box>
                )}

                {items.length === 0 && !preparing ? (
                    <Box className={styles.uploadEmpty}>
                        <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                            Nenhum arquivo escolhido.
                        </Typography>
                        <Typography variant="body2" sx={{ color: ColorPalette.textLight, fontSize: '12px' }}>
                            Imagens são reduzidas automaticamente antes do envio.
                        </Typography>
                    </Box>
                ) : (
                    <Box className={styles.uploadItems}>
                        {items.map((item) => (
                            <Box key={item.id} className={styles.uploadItem}>
                                {GetPreview(item)}

                                <Box className={styles.uploadItemBody}>
                                    <span className={styles.uploadItemName}>{item.name}</span>

                                    {renderItemFields && renderItemFields(
                                        item.file,
                                        item.value,
                                        (value) => updateItemValue(item.id, value)
                                    )}
                                </Box>

                                <IconButton size="small" onClick={() => removeItem(item.id)} disabled={sending} aria-label={`Remover ${item.name}`}>
                                    <IconClose fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}

                {progress && (
                    <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                        {progress}
                    </Typography>
                )}

                <Toast
                    open={toast.open}
                    message={toast.message}
                    severity={toast.severity}
                    onClose={() => setToast({ ...toast, open: false })}
                />
            </Box>
        );
    }

    function GetFooter() {
        return (
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', width: '100%' }}>
                <CustomButton
                    variant="outlined"
                    style="secondary"
                    label="Cancelar"
                    onClick={() => OnHide(false)}
                    enabled={!sending}
                />

                <CustomButton
                    variant="contained"
                    style="primary"
                    label="Salvar"
                    icon={<IconSave />}
                    onClick={saveHandler}
                    loading={sending}
                    enabled={!sending && !preparing && items.length > 0}
                />
            </div>
        );
    }

    return (
        <PopupBase
            Title={title}
            Content={GetContent()}
            Footer={GetFooter()}
            OnClose={() => OnHide(false)}
            maxWidth="md"
        />
    );
}

export default PatientAttachmentUploadPopup;
